import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
} from 'mobx';
import {
  questionnaireCheckTargetKey,
  type QuestionnaireCheckOutcome,
  type QuestionnaireCheckTargetDto,
  type UserQuestionnaireStatusDto,
} from '@web/common';
import {
  checkQuestionnaireForUser,
  getQuestionnaireStatusForUser,
} from '../request/authenticated-requests/user';

// Each check is one request, so no request comes near the proxy timeout.
const CHECK_ALL_CONCURRENCY = 3;

export type QuestionnaireCheckState =
  | { type: 'CHECKING' }
  | { type: 'DONE'; outcome: QuestionnaireCheckOutcome }
  | { type: 'FAILED' };

/** The questionnaires of one participant; another participant gets a new store. */
export class QuestionnaireStatusStore {
  @observable public questionnaires: UserQuestionnaireStatusDto[] = [];
  // true from the start, so the empty state does not show before the first response
  @observable public isLoading = true;
  @observable public loadFailed = false;
  // by questionnaireCheckTargetKey; kept after the reload so the result stays visible
  @observable public checks = new Map<string, QuestionnaireCheckState>();
  @observable public isCheckingAll = false;
  // the view is gone: "check all" stops sending requests
  private isDisposed = false;

  constructor(private readonly userId: string) {
    makeObservable(this);
  }

  @computed public get isAnyCheckRunning(): boolean {
    return (
      this.isCheckingAll ||
      [...this.checks.values()].some((check) => check.type === 'CHECKING')
    );
  }

  @action public load = () => {
    // React runs an effect a second time after its cleanup in strict mode
    this.isDisposed = false;
    this.isLoading = true;
    this.loadFailed = false;
    this.fetch();
  };

  public dispose = () => {
    this.isDisposed = true;
  };

  public checkState = (
    target: QuestionnaireCheckTargetDto,
  ): QuestionnaireCheckState | undefined =>
    this.checks.get(questionnaireCheckTargetKey(target));

  public check = async (target: QuestionnaireCheckTargetDto) => {
    await this.runCheck(target);
    this.fetch();
  };

  public checkAll = async () => {
    const targets = this.questionnaires.flatMap((q) =>
      q.checkable &&
      q.checkTarget &&
      this.checkState(q.checkTarget)?.type !== 'CHECKING'
        ? [q.checkTarget]
        : [],
    );
    runInAction(() => {
      this.isCheckingAll = true;
    });

    const queue = [...targets];
    await Promise.all(
      Array.from({ length: CHECK_ALL_CONCURRENCY }, async () => {
        for (
          let target = queue.shift();
          target && !this.isDisposed;
          target = queue.shift()
        ) {
          await this.runCheck(target);
        }
      }),
    );

    runInAction(() => {
      this.isCheckingAll = false;
    });
    this.fetch();
  };

  private runCheck = async (target: QuestionnaireCheckTargetDto) => {
    const key = questionnaireCheckTargetKey(target);
    runInAction(() => this.checks.set(key, { type: 'CHECKING' }));
    try {
      const { outcome } = await checkQuestionnaireForUser(this.userId, target);
      runInAction(() => this.checks.set(key, { type: 'DONE', outcome }));
    } catch {
      runInAction(() => this.checks.set(key, { type: 'FAILED' }));
    }
  };

  private fetch = () => {
    if (this.isDisposed) return;
    getQuestionnaireStatusForUser(this.userId)
      .then((response) => {
        runInAction(() => {
          this.questionnaires = response.questionnaires;
          this.isLoading = false;
        });
      })
      .catch(() => {
        runInAction(() => {
          this.isLoading = false;
          this.loadFailed = true;
        });
      });
  };
}
